import React, { useEffect, useState, useMemo } from 'react';
import { Button, Flex, message, Space, Spin, Table, Tooltip, TreeSelect, Typography } from "antd";
import Container from "../../components/UI/Container/Container";
import Block from "../../components/UI/Block/Block";
import { API } from "../../http";
import { NEW_ENDPOINTS } from "../../http/endpoints";
import moment from "moment";
import { ReloadOutlined } from "@ant-design/icons";

const getTreeData = (operations) => {
  const tree = {};

  operations.forEach(({ AccountTo }) => {
    if (!AccountTo) return;

    const parts = AccountTo.split('.');
    let currentLevel = tree;

    parts.forEach((part, index) => {
      const fullValue = parts.slice(0, index + 1).join('.');
      if (!currentLevel[part]) {
        currentLevel[part] = {
          value: fullValue,
          label: fullValue,
          children: {},
        };
      }
      currentLevel = currentLevel[part].children;
    });
  });

  const convertTreeToArray = (tree) => {
    return Object.keys(tree).map((key) => {
      const node = tree[key];
      return {
        title: node.label,
        value: node.value,
        children: convertTreeToArray(node.children),
      };
    });
  };

  return convertTreeToArray(tree);
};

const Ledger = () => {
  const [loading, setLoading] = useState(false);
  const [operations, setOperations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [sorterInfo, setSorterInfo] = useState({ field: 'Date', order: 'descend' });
  const [treeOpen, setTreeOpen] = useState(true);

  const sortedOperations = useMemo(() => {
    const ops = {};
    for (let operation of operations) {
      if (ops[operation.TransactionID]) {
        ops[operation.TransactionID].push(operation);
      } else {
        ops[operation.TransactionID] = [operation];
      }
    }
    debugger
    return ops;
  }, [operations]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const {data: {rr}} = await API.post(NEW_ENDPOINTS.ADMIN.USER.GET_LIST);
      return rr;
    } catch (e) {
      message.error('Failed to load users list');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchOperations = async () => {
    setLoading(true);
    try {
      const users = await fetchUsers();
      const { data } = await API.post(NEW_ENDPOINTS.ADMIN.TRANSACTIONS.LEDGER, {
        start: "2024-01-01",
        end: "2025-01-01",
      });
      data.map(item => {
        const user = users.find(user => user.id === item.User);
        if (user) {
          item.UserName = user.first_name + ' ' + user.last_name;
          item.UserEmail = user.email_address;
        }
        return item;
      })
      setOperations(data);
    } catch (e) {
      message.error('Error fetching Ledger History');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperations();
  }, []);

  const treeData = useMemo(() => getTreeData(operations), [operations]);

  const handleAccountSelect = (value) => {
    setSelectedAccount(value);
    setTreeOpen(false);
  };

  const filteredOperations = useMemo(() => {
    if (!selectedAccount) return [];
    return operations.filter(operation => operation.AccountTo.startsWith(selectedAccount));
  }, [selectedAccount, operations]);

  const columns = [
    { title: 'User name', dataIndex: 'UserName', key: 'UserName' },
    { title: 'User email', dataIndex: 'UserEmail', key: 'UserEmail' },
    { title: 'Account To', dataIndex: 'AccountTo', key: 'AccountTo' },
    { title: 'Account From', dataIndex: 'AccountFrom', key: 'AccountFrom' },
    {
      title: 'Amount',
      dataIndex: 'Amount',
      key: 'Amount',
      align: 'center',
      render: (value, record) => <Typography>{record.Amount} {record.Currency}</Typography>,
      sorter: (a, b) => a.Amount - b.Amount,
      sortOrder: sorterInfo.field === 'Amount' ? sorterInfo.order : null,
    },
    { title: 'Currency', dataIndex: 'Currency', key: 'Currency' },
    {
      title: 'Date',
      dataIndex: 'Date',
      key: 'Date',
      align: 'right',
      render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
      sorter: (a, b) => moment(a.Date).diff(moment(b.Date)),
      defaultSortOrder: 'descend',
      sortOrder: sorterInfo.field === 'Date' ? sorterInfo.order : null,
    },
  ];

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: 'Account from',
        dataIndex: 'AccountFrom',
        key: 'AccountTo',
        width: 150,
        render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                            copyable={value}>{value}</Typography.Text>
      },
      {
        title: 'Account to',
        dataIndex: 'AccountTo',
        key: 'AccountTo',
        width: 150,
        render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
      },
      {
        title: 'Amount',
        dataIndex: 'Amount',
        key: 'Amount',
        width: 150,
        render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
      },
      {
        title: 'Currency',
        dataIndex: 'Currency',
        key: 'Currency',
        width: 150,
        render: (value) => <Typography.Text ellipsis={{tooltip: value}}
                                                copyable={value}>{value}</Typography.Text>
      },
    ];
    return <Table scroll={{x: 1000}} columns={columns} dataSource={sortedOperations[record.TransactionID]} pagination={false} bordered />;
  };
  

  const handleTableChange = (pagination, filters, sorter) => {
    setSorterInfo(sorter);
  };

  return (
    <Container>
      <Spin tip="Loading..." spinning={loading}>
        <Block>
          <Typography.Title level={2}>Ledger Operations</Typography.Title>

          <Flex style={{ marginBottom: 20 }} justify="space-between" gap={30} className="w100" align="baseline">
            <TreeSelect
              style={{ width: '100%' }}
              size="large"
              value={selectedAccount}
              dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              treeData={treeData}
              placeholder="Select Account"
              onChange={handleAccountSelect}
              treeDefaultExpandAll
              open={treeOpen}
              onDropdownVisibleChange={(open) => setTreeOpen(open)}
            />
            <Space>
              <Tooltip title="Reload">
                <Button icon={<ReloadOutlined />} onClick={fetchOperations} shape="round" type="primary" />
              </Tooltip>
            </Space>
          </Flex>

          {!selectedAccount ? (
            <Typography.Text type="secondary">Please select an account to view ledger operations.</Typography.Text>
          ) : (
            <Table
              expandable={{
                expandedRowRender,
              }}
              rowKey={(record) => `${record.AccountFrom}-${record.AccountTo}-${record.Date}`}
              dataSource={filteredOperations}
              columns={columns}
              onChange={handleTableChange}
              showSorterTooltip={false}
              pagination={{ position: ['none', 'bottomCenter'], pageSize: 20, hideOnSinglePage: true }}
              sortDirections={['descend', 'ascend']}
            />
          )}
        </Block>
      </Spin>
    </Container>
  );
};

export default Ledger;
